/**
 * HR "Apps & Training" (2026-10-07, Manak) — data for the Mobile Apps page, the
 * App Tour Progress page and their two preview boxes on the People Desk.
 *
 * Backend: GET /hr/app-overview/releases and /hr/app-overview/tour-progress
 * (HR + admin only). The server sends raw rows (real, current employees only);
 * the maths is done here.
 *
 * 2026-10-08 (Manak): phone and web are two separate tours, each counted with
 * the SAME rule the app that shows it uses. Nobody is measured against a tour
 * their app does not have.
 *
 *   Phone tour — the lesson list of the AURA Sales app (Android + Both lessons),
 *     filtered by the app's own rule (aura_sales lib/features/tour/data/
 *     tour_logic.dart: tourRoleFor + canSeeLesson, mirrored in phoneTourRole /
 *     phoneLessonIds below). Only the Sales app has a tour: Triage, Production,
 *     Inventory and Service have none. A person gets a phone tour when their app
 *     is the Sales app, or when they can open the web App Tour (its "Android
 *     app" switch plays the same lessons — that is how the admin sees them).
 *   Web tour — the web App Tour (/tour): Web + Both lessons for their audience
 *     (tourCatalog.canSeeLesson + onPlatform). Only people whose menu opens
 *     /tour have it (canOpenProfileRoute — P01, P02, HR (P17, since 2026-10-08:
 *     the HR lessons only, no phone tour) and the admin).
 *   No tour in a column → "No tour" chip, left out of every total and filter.
 *   Overall = both applicable columns together, each lesson once (a "Both"
 *   lesson sits in both lists and is one tick).
 *   done = ticked lesson ids inside that list. Ticks outside it are not counted.
 */
import { z } from 'zod';
import { useProfileResource } from '@/pages/dashboard/roles/useProfileResource';
import { TOUR_LESSONS, TOUR_MODULES } from '@/pages/tour/tourCatalog.generated';
import { canSeeLesson, onPlatform, tourAudience } from '@/pages/tour/tourCatalog';
import type { TourLesson, TourModule } from '@/pages/tour/tourTypes';
import { canOpenProfileRoute } from '@/lib/profileAccess';
import { isSalesIdentity } from '@/lib/profileIdentity';
import { DEALER_TRIAGE_ROLES, normalizeRoleName } from '@/lib/roles';

const isoDate = z.string().refine((v) => Number.isFinite(Date.parse(v)));

const appSchema = z.object({
  key: z.string(),
  name: z.string(),
  versionName: z.string(),
  versionCode: z.number(),
  forced: z.boolean(),
  apkUrl: z.string(),
  releasedAt: isoDate.nullable(),
});
export type MobileApp = z.infer<typeof appSchema>;
const releasesSchema = z.object({ apps: z.array(appSchema) });

const employeeSchema = z.object({
  userId: z.string(),
  name: z.string(),
  role: z.string().nullable(),
  designation: z.string().nullable(),
  team: z.string().nullable(),
  done: z.array(z.object({ lessonId: z.string(), doneAt: isoDate.nullable() })),
  appVersion: z.string().nullable(),
  appVersionSeenAt: isoDate.nullable(),
});
export type TourEmployee = z.infer<typeof employeeSchema>;
const progressSchema = z.object({ employees: z.array(employeeSchema) });

const parseReleases = (b: any) => releasesSchema.parse(b?.data).apps;
const parseProgress = (b: any) => progressSchema.parse(b?.data).employees;
export const useMobileApps = () => useProfileResource('/hr/app-overview/releases', parseReleases);
export const useTourProgress = () => useProfileResource('/hr/app-overview/tour-progress', parseProgress);

/** '8.14.197+81597' → { name: '8.14.197', code: 81597 }. Anything else keeps its text, code null. */
export function parseAppVersion(raw: string | null): { name: string; code: number | null } | null {
  if (!raw || !raw.trim()) return null;
  const [name, build] = raw.trim().split('+');
  const code = build && /^\d+$/.test(build) ? Number(build) : null;
  return { name: name || raw.trim(), code };
}

// ─── Which mobile app a person uses ─────────────────────────────────────────
// Manifest key (app-releases.json, same keys as GET /releases). There is no
// single role→app table in the codebase, so each app's own rule is used:
//   sunstellar (AURA Sales)  — the sales roles (profileIdentity.isSalesIdentity).
//   triage (AURA Triage)     — the triage coordinator (roles.DEALER_TRIAGE_ROLES):
//                              the Sales app's triage flavour.
//   production               — aura_production config/production_role_routing.dart
//                              productionRoleFor: production, production_manager,
//                              qa_manager, qc_inspector.
//   inventory                — aura_inventory app/role_home.dart: guards (security)
//                              → gate, loaders → dispatch; the store keeper is the
//                              app's home user (GRN, stock).
//   service                  — service_tech, the Service app's role (Phase 2
//                              service-ticket roles).
//   null                     — every other role works on the web only (admin, HR,
//                              buyer, accounts, CRM operations, dispatch manager).
export type AppKey = 'sunstellar' | 'triage' | 'production' | 'inventory' | 'service';
export const APP_NAMES: Record<AppKey, string> = {
  sunstellar: 'AURA Sales', triage: 'AURA Triage', production: 'AURA Production', inventory: 'AURA Inventory', service: 'AURA Service',
};
const PRODUCTION_APP_ROLES = new Set(['production', 'production_manager', 'qa_manager', 'qc_inspector']);
const INVENTORY_APP_ROLES = new Set(['store_keeper', 'security', 'loader']);
const SERVICE_APP_ROLES = new Set(['service_tech']);
/** Apps that stamp their version on location pings (the aura_sales code base). The others send none yet. */
const VERSION_REPORTING_APPS = new Set<AppKey>(['sunstellar', 'triage']);

export function mobileAppFor(person: { role: string | null; designation?: string | null }): AppKey | null {
  const role = normalizeRoleName(person.role);
  if ((DEALER_TRIAGE_ROLES as readonly string[]).includes(role)) return 'triage';
  if (PRODUCTION_APP_ROLES.has(role)) return 'production';
  if (INVENTORY_APP_ROLES.has(role)) return 'inventory';
  if (SERVICE_APP_ROLES.has(role)) return 'service';
  if (isSalesIdentity({ role: person.role ? { name: person.role } : null, designation: person.designation ?? null })) return 'sunstellar';
  return null;
}

// ─── Phone tour: the AURA Sales app's own rule (tour_logic.dart) ────────────
export type PhoneTourRole = 'rep' | 'coordinator' | 'manager' | 'admin';
// owner_filter.dart _kTeamHeadRoles / _kCoordinatorRoles; dealer_access_requests.dart admin tier.
const PHONE_ADMIN = new Set(['owner', 'admin', 'super_admin']);
const PHONE_TEAM_HEAD = new Set(['manager', 'team_head', 'sales_manager', 'sr_sales_manager', 'sales_head', 'assistant_executive_sales', 'admin', 'owner', 'lead_manager', 'operations_manager']);
const PHONE_COORDINATOR = new Set(['sales_coordinator', 'coordinator']);

/** tour_logic.dart tourRoleFor: admin tier first, then team heads, then coordinators. */
export function phoneTourRole(role: string | null): PhoneTourRole {
  const r = normalizeRoleName(role);
  if (PHONE_ADMIN.has(r)) return 'admin';
  if (PHONE_TEAM_HEAD.has(r)) return 'manager';
  if (PHONE_COORDINATOR.has(r)) return 'coordinator';
  return 'rep';
}

const MANAGER_ONLY = /^manager only/i;
/**
 * tour_logic.dart canSeeLesson: Android or Both lessons; admin-only → admin;
 * manager-only (its "Who" or its module title starts "Manager only") → not a rep.
 */
export function phoneLessonIds(role: PhoneTourRole, lessons: TourLesson[] = TOUR_LESSONS, modules: TourModule[] = TOUR_MODULES): Set<string> {
  const titles = new Map(modules.map((m) => [m.id, m.title]));
  return new Set(lessons.filter((l) => {
    if (l.platform === 'Web') return false;
    if (l.adminOnly) return role === 'admin';
    if (MANAGER_ONLY.test(l.who) || MANAGER_ONLY.test(titles.get(l.moduleId) ?? '')) return role !== 'rep';
    return true;
  }).map((l) => l.id));
}

export interface TourAccess {
  app: AppKey | null;
  /** null = no phone tour for this person. */
  phone: Set<string> | null;
  /** null = no web tour for this person. */
  web: Set<string> | null;
}

/** Which tours this person has, and the lesson ids in each. */
export function tourAccess(person: { role: string | null; designation: string | null }, lessons: TourLesson[] = TOUR_LESSONS, modules: TourModule[] = TOUR_MODULES): TourAccess {
  const user = { id: 'x', tenantId: 't', role: person.role ? { name: person.role } : null, designation: person.designation };
  const app = mobileAppFor(person);
  const webOpen = canOpenProfileRoute(user, '/tour', '');
  const audience = tourAudience(user);
  return {
    app,
    // HR's web tour has no phone lessons (its "Android app" switch is hidden), so HR has no phone tour.
    phone: app === 'sunstellar' || (webOpen && audience !== 'hr') ? phoneLessonIds(phoneTourRole(person.role), lessons, modules) : null,
    web: webOpen ? new Set(lessons.filter((l) => canSeeLesson(l, audience) && onPlatform(l, 'web')).map((l) => l.id)) : null,
  };
}

export interface TourColumn {
  done: number;
  total: number;
  /** 0–100, whole number, floored so 99.6% never reads as finished. */
  pct: number;
}

export interface TourProgressRow {
  userId: string;
  name: string;
  role: string | null;
  roleLabel: string;
  team: string | null;
  /** null = this person's apps have no phone tour. */
  phone: TourColumn | null;
  /** null = no web tour. */
  web: TourColumn | null;
  /** Both applicable columns together, each lesson once. null = no tour at all. */
  overall: TourColumn | null;
  last: { lessonId: string; title: string; doneAt: string | null } | null;
  app: {
    key: AppKey | null;
    /** 'AURA Inventory', or 'Web only'. */
    name: string;
    /** Installed version, when the app reports one. */
    version: { name: string; code: number | null; old: boolean } | null;
    reportsVersion: boolean;
  };
}

export function roleLabel(role: string | null): string {
  if (!role) return 'No role';
  return role.replace(/[_-]+/g, ' ').trim().replace(/\b\w/g, (c) => c.toUpperCase());
}

const column = (set: Set<string>, done: Set<string>): TourColumn => {
  let n = 0;
  for (const id of set) if (done.has(id)) n++;
  return { done: n, total: set.size, pct: set.size ? Math.floor((n * 100) / set.size) : 0 };
};

export function tourProgressRows(
  employees: TourEmployee[],
  apps: Array<{ key: string; versionCode: number }> | null,
  lessons: TourLesson[] = TOUR_LESSONS,
  modules: TourModule[] = TOUR_MODULES,
): TourProgressRow[] {
  const byId = new Map(lessons.map((l) => [l.id, l]));
  const latestCode = new Map((apps ?? []).map((a) => [a.key, a.versionCode]));
  return employees.map((e) => {
    const access = tourAccess(e, lessons, modules);
    const all = new Set([...(access.phone ?? []), ...(access.web ?? [])]);
    const ticks = new Map(e.done.filter((d) => all.has(d.lessonId)).map((d) => [d.lessonId, d]));
    const done = new Set(ticks.keys());
    const latest = [...ticks.values()].sort((a, b) => (Date.parse(b.doneAt ?? '') || 0) - (Date.parse(a.doneAt ?? '') || 0))[0];
    const reportsVersion = access.app != null && VERSION_REPORTING_APPS.has(access.app);
    const v = reportsVersion ? parseAppVersion(e.appVersion) : null;
    const code = access.app ? latestCode.get(access.app) : undefined;
    return {
      userId: e.userId,
      name: e.name,
      role: e.role,
      roleLabel: roleLabel(e.role),
      team: e.team,
      phone: access.phone ? column(access.phone, done) : null,
      web: access.web ? column(access.web, done) : null,
      overall: all.size ? column(all, done) : null,
      last: latest ? { lessonId: latest.lessonId, title: byId.get(latest.lessonId)?.title ?? latest.lessonId, doneAt: latest.doneAt } : null,
      app: {
        key: access.app,
        name: access.app ? APP_NAMES[access.app] : 'Web only',
        version: v ? { ...v, old: code != null && v.code != null && v.code < code } : null,
        reportsVersion,
      },
    };
  });
}

export interface TourTotals { employees: number; started: number; finished: number; notStarted: number; noTour: number }
/** Counts people who have a tour; people with no tour in either column are only counted in noTour. */
export function tourTotals(rows: TourProgressRow[]): TourTotals {
  const withTour = rows.filter((r) => r.overall);
  const started = withTour.filter((r) => r.overall!.done > 0).length;
  return {
    employees: withTour.length,
    started,
    finished: withTour.filter((r) => r.overall!.total > 0 && r.overall!.done >= r.overall!.total).length,
    notStarted: withTour.length - started,
    noTour: rows.length - withTour.length,
  };
}

export type ProgressFilter = 'all' | 'not-started' | 'below-50' | 'finished';
/** Progress filters read the overall figure and only ever list people who have a tour. */
export function filterRows(rows: TourProgressRow[], f: { progress: ProgressFilter; role: string; team: string }): TourProgressRow[] {
  return rows.filter((r) => {
    if (f.role && (r.role ?? '') !== f.role) return false;
    if (f.team && (r.team ?? '') !== f.team) return false;
    if (f.progress === 'all') return true;
    const o = r.overall;
    if (!o) return false;
    if (f.progress === 'not-started') return o.done === 0;
    if (f.progress === 'below-50') return o.pct < 50;
    return o.total > 0 && o.done >= o.total;
  });
}

/** Lowest overall progress first; people with no tour last; ties by name. */
export function lowestFirst(rows: TourProgressRow[]): TourProgressRow[] {
  const key = (r: TourProgressRow) => (r.overall ? [0, r.overall.pct, r.overall.done] : [1, 0, 0]);
  return [...rows].sort((a, b) => {
    const [x, y] = [key(a), key(b)];
    return x[0] - y[0] || x[1] - y[1] || x[2] - y[2] || a.name.localeCompare(b.name);
  });
}

export const shortDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short' }) : '';

export const NO_TOUR = 'No tour';

/** Rows for the Excel download, in the order shown on screen. */
export function excelRows(rows: TourProgressRow[]) {
  const cols = (label: string, c: TourColumn | null) => ({
    [`${label} tour %`]: c ? c.pct : NO_TOUR,
    [`${label} lessons done`]: c ? c.done : '',
    [`${label} lessons for role`]: c ? c.total : '',
  });
  return rows.map((r) => ({
    Employee: r.name,
    Role: r.roleLabel,
    Team: r.team ?? '',
    App: r.app.name,
    'App version': r.app.version ? `${r.app.version.name}${r.app.version.old ? ' (old)' : ''}` : '',
    ...cols('Phone', r.phone),
    ...cols('Web', r.web),
    'Overall %': r.overall ? r.overall.pct : NO_TOUR,
    'Last lesson watched': r.last ? `${r.last.lessonId} ${r.last.title}` : '',
    'Last watched on': r.last?.doneAt ? shortDate(r.last.doneAt) : '',
  }));
}

export const APP_VERSION_UNKNOWN_HINT =
  'Shows after the app on this person\'s phone sends its version. Only phones that share location send it today.';
export const APP_NO_VERSION_HINT = 'This app does not send its version yet.';
export const NO_TOUR_HINT = 'This person\'s app has no App Tour, so it is not counted.';

